import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  PhoneIcon,
  ChartBarIcon,
  PlusIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from 'react-hot-toast';

function Dashboard() {
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [callSessions, setCallSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalCalls: 0,
    activeCalls: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [agentsResponse, phoneCallsResponse] = await Promise.all([
        api.get('/agents'),
        api.get('/phone-calls/sessions'),
      ]);

      setAgents(agentsResponse.data);
      const allSessions = phoneCallsResponse.data.sessions || [];
      setCallSessions(allSessions);

      // Calculate stats
      const totalAgents = agentsResponse.data.length;
      const activeAgents = agentsResponse.data.filter(agent => agent.is_active).length;
      const totalCalls = allSessions.length;
      const activeCalls = allSessions.filter(call => call.status === 'connected' || call.status === 'initiated').length;

      setStats({
        totalAgents,
        activeAgents,
        totalCalls,
        activeCalls,
      });
    } catch (error) {
      toast.error(t('errors.failedFetchDashboard'));
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
      case 'initiated':
        return <PlayIcon className="h-5 w-5 text-primary-500" />;
      case 'ended':
        return <CheckCircleIcon className="h-5 w-5 text-success-500" />;
      default:
        return <ClockIcon className="h-5 w-5" style={{ color: 'var(--muted)' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
      case 'initiated':
        return 'status-calling';
      case 'ended':
        return 'status-active';
      default:
        return 'status-inactive';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl sm:truncate">
            {t('dashboard.title')}
          </h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            {t('dashboard.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/agents/new"
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('dashboard.newAgent')}
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>
                  {t('dashboard.totalAgents')}
                </dt>
                <dd className="text-lg font-medium text-foreground">
                  {stats.totalAgents}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-success-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>
                  {t('dashboard.activeAgents')}
                </dt>
                <dd className="text-lg font-medium text-foreground">
                  {stats.activeAgents}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PhoneIcon className="h-8 w-8 text-warning-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>
                  {t('dashboard.totalCalls')}
                </dt>
                <dd className="text-lg font-medium text-foreground">
                  {stats.totalCalls}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PlayIcon className="h-8 w-8 text-danger-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium truncate" style={{ color: 'var(--muted)' }}>
                  {t('dashboard.activeCalls')}
                </dt>
                <dd className="text-lg font-medium text-foreground">
                  {stats.activeCalls}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Agents */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">{t('dashboard.recentAgents')}</h3>
            <Link
              to="/agents"
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              {t('common.viewAll')}
            </Link>
          </div>
          <div className="space-y-3">
            {agents.slice(0, 5).map((agent) => (
              <div key={agent.id} className="flex items-center justify-between p-3 bg-background rounded-xl">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-foreground">{agent.name}</p>
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>{agent.voice}</p>
                  </div>
                </div>
                <span className={`status-badge ${agent.is_active ? 'status-active' : 'status-inactive'}`}>
                  {agent.is_active ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            ))}
            {agents.length === 0 && (
              <div className="text-center py-6">
                <UserGroupIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
                <h3 className="mt-2 text-sm font-medium text-foreground">{t('dashboard.noAgents')}</h3>
                <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{t('dashboard.getStartedAgents')}</p>
                <div className="mt-6">
                  <Link to="/agents/new" className="btn-primary">
                    <PlusIcon className="h-5 w-5 mr-2" />
                    {t('dashboard.newAgent')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Calls */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">{t('dashboard.recentCalls')}</h3>
            <Link
              to="/calls"
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              {t('common.viewAll')}
            </Link>
          </div>
          <div className="space-y-3">
            {callSessions.slice(0, 5).map((call) => (
              <div key={call.session_id} className="flex items-center justify-between p-3 bg-background rounded-xl">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {getStatusIcon(call.status)}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-foreground">{call.phone_number}</p>
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>
                      {new Date(call.started_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <span className={`status-badge ${getStatusColor(call.status)}`}>
                  {call.status}
                </span>
              </div>
            ))}
            {callSessions.length === 0 && (
              <div className="text-center py-6">
                <PhoneIcon className="mx-auto h-12 w-12" style={{ color: 'var(--muted)' }} />
                <h3 className="mt-2 text-sm font-medium text-foreground">{t('dashboard.noCalls')}</h3>
                <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>{t('dashboard.getStartedCalls')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-medium text-foreground mb-4">{t('dashboard.quickActions')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/agents/new"
            className="flex items-center p-4 border border-border rounded-xl hover:bg-background transition-colors"
          >
            <PlusIcon className="h-8 w-8 text-primary-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-foreground">{t('dashboard.createAgent')}</p>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>{t('dashboard.addNewAgent')}</p>
            </div>
          </Link>

          <Link
            to="/calls"
            className="flex items-center p-4 border border-border rounded-xl hover:bg-background transition-colors"
          >
            <PhoneIcon className="h-8 w-8 text-success-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-foreground">{t('dashboard.makeCall')}</p>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>{t('dashboard.initiateNewCall')}</p>
            </div>
          </Link>

          <Link
            to="/analytics"
            className="flex items-center p-4 border border-border rounded-xl hover:bg-background transition-colors"
          >
            <ChartBarIcon className="h-8 w-8 text-warning-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-foreground">{t('dashboard.viewAnalytics')}</p>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>{t('dashboard.checkPerformance')}</p>
            </div>
          </Link>

        </div>
      </div>
    </div>
  );
}

export default Dashboard;